import { useCallback, useState } from 'react';

import { handleApiError } from 'api/error-handling';
import { getBlockingInvoiceErrorData } from 'api/error-handling/domains/athlete-packages';
import type { ApiErrorResponse } from 'types/api-error';
import notificationHandler from 'utils/notifications';

interface UseBlockingInvoiceErrorOptions {
  nonBlockingErrorTitle: string;
  onNonBlockingError?: () => void;
}

interface BlockingInvoiceModalData {
  message: string;
  isChildPackageError: boolean;
  blockingInvoiceId: string | null;
}

interface UseBlockingInvoiceErrorResult {
  showErrorModal: boolean;
  errorModalData: BlockingInvoiceModalData | null;
  handleError: (error: unknown) => void;
  resetError: () => void;
}

export function useBlockingInvoiceError(
  options: UseBlockingInvoiceErrorOptions,
): UseBlockingInvoiceErrorResult {
  const { nonBlockingErrorTitle, onNonBlockingError } = options;
  const { errorNotification } = notificationHandler();

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState<BlockingInvoiceModalData | null>(null);

  const handleError = useCallback(
    (error: unknown) => {
      const apiError: ApiErrorResponse = handleApiError(error);
      const blockingData = getBlockingInvoiceErrorData(apiError);

      if (blockingData) {
        setErrorModalData({
          message: apiError.message,
          isChildPackageError: blockingData.isChildPackageError,
          blockingInvoiceId: blockingData.blockingInvoiceId,
        });
        setShowErrorModal(true);
      } else {
        errorNotification(nonBlockingErrorTitle, apiError.message);
        onNonBlockingError?.();
      }
    },
    [errorNotification, nonBlockingErrorTitle, onNonBlockingError],
  );

  const resetError = useCallback(() => {
    setShowErrorModal(false);
    setErrorModalData(null);
  }, []);

  return {
    showErrorModal,
    errorModalData,
    handleError,
    resetError,
  };
}
